import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { SnackbarProvider } from "notistack";

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import LayoutApp from "@/layoutApp";
import ErrorBoundary from "@/components/error-boundary";
import ErrorView from "@/sections/error/error-view";
import { storeLog } from "@/api/lookup";
import useTenantConfig from "@/hooks/useTenantConfig";
import { useEffect, useState } from 'react';

function App() {
  const { enableClientLog } = useTenantConfig();
  const [fatalError, setFatalError] = useState(false);

  useEffect(() => {
    if (!enableClientLog) return;

    // Listener globale per errori JavaScript non catturati
    const handleError = (event) => {
      if (!event?.message || event.message === 'Script error.') return;

      // Ignora errori provenienti da script esterni o bundle in cache vecchi
      const appDomain = (process.env.MIX_APP_URL || '').replace(/^https?:\/\//, '').split('/')[0];
      const isYourCode = appDomain && event?.filename?.includes(appDomain);
      if (!isYourCode) return;

      const shortMessage = event.message.length > 50
        ? event.message.substring(0, 50) + '...'
        : event.message;

      storeLog(`Global JS Error: "${shortMessage}"`, 'warning', {
        fullMessage: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error ? event.error.stack : 'N/A',
      });

      // Triggera ErrorView solo per errori del tuo codice
      setTimeout(() => setFatalError(true), 0);
    };

    // Listener globale per promesse (async/await) rifiutate e non gestite
    const handleUnhandledRejection = (event) => {
      const reason = event?.reason;
      const reasonMessage = reason instanceof Error 
        ? reason.message 
        : String(reason ?? 'unknown');
      
      // Non mostrare ErrorView per errori di rete — sono gestiti localmente
      const isNetworkError = [
        'Failed to fetch',
        'Load failed',
        'Connection timed out',
        'session_expired',
        'NetworkError',
      ].some(pattern => reasonMessage.includes(pattern));

      const shortMessage = reasonMessage.length > 30 
        ? reasonMessage.substring(0, 30) + '...' 
        : reasonMessage;

      storeLog(`Unhandled Promise Rejection: "${shortMessage}"`, (isNetworkError ? 'error' : 'warning'), {
        reason: reason instanceof Error ? reason.stack : reasonMessage,
        message: reasonMessage,
        filename: reason instanceof Error ? reason?.filename : 'N/A',
        lineno: reason instanceof Error ? reason?.lineno : 'N/A',
        colno: reason instanceof Error ? reason?.colno : 'N/A',
        isNetworkError: isNetworkError,
      });

      if (!isNetworkError) {
        setTimeout(() => setFatalError(true), 0);  // ← solo per errori non di rete
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [enableClientLog]);

  return (
    <ErrorBoundary enabled={enableClientLog}>
      <SnackbarProvider>
        <HelmetProvider>
          <BrowserRouter>
            {fatalError ? (
              <ErrorView />
            ) : (
              <Suspense fallback={<React.Fragment />}>
                <LayoutApp />
              </Suspense>
            )}
          </BrowserRouter>
        </HelmetProvider>
      </SnackbarProvider>
    </ErrorBoundary>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(<App />);

/*
createInertiaApp({
    resolve: name => require(`@/Pages/${name}`),
    setup({ el, App, props }) {
        createRoot(el).render(<App {...props} />)
    },
})
*/
